import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Shield } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";

/** Simplified registration form for Airbnb / short-term-rental hosts.
 *  Shares the same backend endpoint as HotelRegister but flags the account
 *  as `account_type: "airbnb"` and ships a default `booking_frequency` so
 *  the existing schema remains backwards-compatible. */
export default function AirbnbRegister() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    host_name: "",
    listing_address: "",
    phone: "",
    contact_email: "",
    password: "",
    password_confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (form.password !== form.password_confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      // Map onto the existing /auth/register-hotel schema.
      const payload = {
        hotel_name: form.host_name,
        hotel_address: form.listing_address,
        manager_name: form.host_name,
        phone: form.phone,
        contact_email: form.contact_email,
        password: form.password,
        booking_frequency: "1-5",
        account_type: "airbnb",
      };
      const { data } = await api.post("/auth/register-hotel", payload);
      await setSession(data);
      toast.success("Compte créé — bienvenue sur Saor !");
      nav("/dashboard/hotel", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 sm:py-16 md:grid-cols-12 lg:px-10" data-testid="airbnb-register-page">
      <aside className="md:col-span-5">
        <div className="sticky top-24 rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Home className="h-5 w-5" />
          </div>
          <h1 className="mt-5 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">Inscription Airbnb</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            En 30 secondes, créez votre compte Saor pour réserver un chauffeur premium pour vos voyageurs.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> Aucun engagement, aucune carte requise.</li>
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> Accès immédiat après inscription.</li>
            <li className="flex items-start gap-2"><Shield className="mt-0.5 h-4 w-4 text-primary" /> L'adresse de votre logement est pré-remplie à chaque course.</li>
          </ul>
        </div>
      </aside>

      <form onSubmit={submit} className="md:col-span-7 space-y-5" data-testid="airbnb-register-form">
        <Field label="Nom de l'hébergeur" required>
          <Input
            value={form.host_name}
            onChange={upd("host_name")}
            required
            data-testid="airbnb-host-name"
            placeholder="Camille Martin"
          />
        </Field>

        <Field label="Adresse du logement" required>
          <AddressAutocomplete
            value={form.listing_address}
            onChange={(v) => setForm((f) => ({ ...f, listing_address: v }))}
            required
            data-testid="airbnb-address"
            placeholder="37 Promenade des Anglais, 06000 Nice"
          />
        </Field>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Numéro de téléphone" required>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              data-testid="airbnb-phone"
            />
          </Field>
          <Field label="Adresse mail" required>
            <Input
              type="email"
              value={form.contact_email}
              onChange={upd("contact_email")}
              required
              data-testid="airbnb-email"
              placeholder="vous@exemple.com"
            />
          </Field>
        </div>

        <Field label="Mot de passe" required>
          <Input
            type="password"
            value={form.password}
            onChange={upd("password")}
            minLength={6}
            required
            data-testid="airbnb-password"
            placeholder="Minimum 6 caractères"
          />
        </Field>

        <Field label="Confirmer le mot de passe" required>
          <Input
            type="password"
            value={form.password_confirm}
            onChange={upd("password_confirm")}
            minLength={6}
            required
            data-testid="airbnb-password-confirm"
            placeholder="Retapez votre mot de passe"
          />
        </Field>

        <div className="pt-4">
          <Button
            type="submit"
            size="lg"
            disabled={loading}
            className="w-full rounded-full bg-primary hover:bg-primary/90 sm:w-auto sm:px-10"
            data-testid="airbnb-submit"
          >
            {loading ? "Création..." : "Créer mon compte"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label} {required && <span className="text-accent">*</span>}</Label>
      {children}
    </div>
  );
}
